// iter_130 (2026-06-13) — Online Order Popup Watcher.
//
// Mounted once inside Layout.jsx so it runs on every authenticated
// staff page (POS, KDS, Orders, Settings, Dashboard, etc.).
//
// Behaviour:
//   * Poll GET /api/orders/recent-public?venue_id=&since= every 10s.
//   * `lastSeenAt` starts at "now on mount" — staff never get a backlog
//     flood on arrival; only orders that land AFTER login surface.
//   * Single-flight guard so two cycles cannot race.
//   * Pause polling while the document is hidden; resume on
//     visibilitychange.
//   * Dedup popups via:
//       1. `since` filter (server-side).
//       2. Per-device localStorage seen-id set (24h prune, 500 cap).
//       3. In-memory queue dedupe.
//   * One popup at a time. Queue the rest. Show next after the current
//     is dismissed/actioned.
//   * Three actions: View Order → /orders?search=<ticket>; Open KDS →
//     /kds; Dismiss. All three mark the order as seen + advance queue.
//   * No auto-dismiss timeout — popups stay visible until acknowledged.
//
// Strictly additive: never mutates orders, never touches payment, KDS,
// printing or any business workflow. Read-only watcher.
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Bell, ShoppingCart, MonitorPlay, Warning, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const POLL_MS = 10000;
const SEEN_MAX = 500;
const SEEN_PRUNE_MS = 24 * 60 * 60 * 1000; // 24h
const LS_KEY = (venueId) => `mm_seen_public_order_popups:${venueId || "default"}`;

// ============================================================
// localStorage helpers
// ============================================================
function _readSeen(venueId) {
  try {
    const raw = localStorage.getItem(LS_KEY(venueId));
    if (!raw) return { entries: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.entries)) return { entries: [] };
    const cutoff = Date.now() - SEEN_PRUNE_MS;
    const kept = parsed.entries.filter((e) => e && e.ts && e.ts > cutoff);
    return { entries: kept.slice(-SEEN_MAX) };
  } catch (_e) { return { entries: [] }; }
}

function _writeSeen(venueId, store) {
  try {
    const trimmed = { entries: (store.entries || []).slice(-SEEN_MAX) };
    localStorage.setItem(LS_KEY(venueId), JSON.stringify(trimmed));
  } catch (_e) { /* private mode / quota — best effort */ }
}

function _markSeen(venueId, orderId) {
  const store = _readSeen(venueId);
  if (store.entries.find((e) => e.id === orderId)) return;
  store.entries.push({ id: orderId, ts: Date.now() });
  _writeSeen(venueId, store);
}

function _seenIdSet(venueId) {
  return new Set(_readSeen(venueId).entries.map((e) => e.id));
}

// ============================================================
// Display helpers
// ============================================================
const SOURCE_TITLE = { online: "NEW ONLINE ORDER", qr: "NEW QR ORDER", kiosk: "NEW KIOSK ORDER" };
const SOURCE_LABEL = { online: "Online Order", qr: "QR Order", kiosk: "Kiosk Order" };

function _orderTypeLabel(t) {
  switch ((t || "").toLowerCase()) {
    case "delivery": return "Delivery";
    case "pickup": return "Pickup";
    case "dine_in": return "Dine In";
    case "takeaway": return "Takeaway";
    case "preorder": return "Preorder";
    default: return t || "—";
  }
}

const PAYMENT_LABELS = {
  paid: "Paid",
  pending: "Pay At Counter",
  pending_collection: "Pay Driver Cash",
  unpaid: "Awaiting Payment",
  failed: "Payment Failed",
  partially_paid: "Partially Paid",
};
const UNPAID_STATES = new Set(["pending", "pending_collection", "unpaid", "failed", "partially_paid"]);

function _paymentLabel(status, method) {
  const base = PAYMENT_LABELS[status] || (status || "—");
  // Friendly Stripe note when a public order paid via online_card.
  if (status === "paid" && (method === "online_card" || method === "stripe")) {
    return "Paid by Stripe";
  }
  return base;
}

function _fmtScheduled(iso) {
  if (!iso) return null;
  try {
    const dt = new Date(iso);
    const time = dt.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", hour12: true })
      .replace(/\s/g, "").toLowerCase();
    const day = dt.toLocaleDateString("en-AU", { weekday: "short" });
    const date = dt.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });
    return `${time} ${day} ${date}`;
  } catch (_e) { return iso; }
}

function _fmtTotal(t) {
  const n = typeof t === "number" ? t : parseFloat(t || 0);
  if (Number.isNaN(n)) return "$0.00";
  return `$${n.toFixed(2)}`;
}

// ============================================================
// Component
// ============================================================
export function OnlineOrderPopupWatcher({ venueId, role }) {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]); // [{order...}]
  const inflightRef = useRef(false);
  const lastSeenAtRef = useRef(new Date().toISOString()); // "now on mount"
  const venueIdRef = useRef(venueId);
  useEffect(() => { venueIdRef.current = venueId; }, [venueId]);

  // Skip the watcher entirely for delivery_driver role — they don't
  // take new orders.
  const enabled = !!venueId && role !== "delivery_driver";

  const tick = useCallback(async () => {
    if (!enabled) return;
    if (inflightRef.current) return;
    if (typeof document !== "undefined" && document.hidden) return;
    inflightRef.current = true;
    try {
      const r = await api.get("/orders/recent-public", {
        params: { venue_id: venueIdRef.current, since: lastSeenAtRef.current, limit: 25 },
      });
      const rows = Array.isArray(r?.data) ? r.data : [];
      if (rows.length) {
        // Advance lastSeenAt to the NEWEST created_at we received so
        // subsequent polls don't return the same rows. Backend sorts
        // DESC, so rows[0] is newest.
        const newest = rows[0]?.created_at;
        if (newest && newest > lastSeenAtRef.current) {
          lastSeenAtRef.current = newest;
        }
        const seen = _seenIdSet(venueIdRef.current);
        setQueue((prev) => {
          const queuedIds = new Set(prev.map((o) => o.id));
          // Oldest first when surfacing → reverse the DESC response.
          const fresh = [];
          for (const o of rows.slice().reverse()) {
            if (!o?.id) continue;
            if (seen.has(o.id)) continue;
            if (queuedIds.has(o.id)) continue;
            fresh.push(o);
          }
          if (!fresh.length) return prev;
          return [...prev, ...fresh];
        });
      }
    } catch (_e) {
      // Silent — next cycle retries.
    } finally {
      inflightRef.current = false;
    }
  }, [enabled]);

  // Polling loop + visibility pause/resume.
  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    const timer = setInterval(() => { if (!stopped) tick(); }, POLL_MS);
    // Resume immediately when the tab returns to foreground.
    const onVis = () => { if (!document.hidden && !stopped) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled, tick]);

  const active = queue[0] || null;

  const advance = useCallback((orderId) => {
    if (orderId) _markSeen(venueIdRef.current, orderId);
    setQueue((prev) => prev.slice(1));
  }, []);

  const onDismiss = useCallback(() => {
    if (active) advance(active.id);
  }, [active, advance]);

  const onViewOrder = useCallback(() => {
    if (!active) return;
    const ticket = active.ticket_number;
    advance(active.id);
    if (ticket != null) navigate(`/orders?search=${encodeURIComponent(ticket)}`);
    else navigate("/orders");
  }, [active, advance, navigate]);

  const onOpenKDS = useCallback(() => {
    if (!active) return;
    advance(active.id);
    navigate("/kds");
  }, [active, advance, navigate]);

  if (!enabled || !active) return null;

  const title = SOURCE_TITLE[active.source] || "NEW ORDER";
  const sourceLabel = SOURCE_LABEL[active.source] || (active.source || "Order");
  const orderTypeLabel = _orderTypeLabel(active.order_type);
  const isPreorder = active.fulfilment_type === "scheduled" || !!active.scheduled_for;
  const isUnpaid = UNPAID_STATES.has(active.payment_status);
  const paymentLabel = _paymentLabel(active.payment_status, active.payment_method);
  const scheduledStr = _fmtScheduled(active.scheduled_for);
  const customerName = active.customer?.name || "";

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onDismiss(); }}>
      <DialogContent
        className="max-w-md"
        data-testid="online-order-popup"
        data-order-id={active.id}
        data-source={active.source}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Bell size={18} weight="fill" className="text-[var(--terracotta)]" />
            <span data-testid="online-order-popup-title">{title}</span>
          </DialogTitle>
          <DialogDescription>
            A new customer-facing order has just been placed.
          </DialogDescription>
        </DialogHeader>

        {isUnpaid && (
          <div
            data-testid="online-order-payment-warning"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 flex items-center gap-2"
          >
            <Warning size={16} weight="fill" className="text-red-700 shrink-0" />
            <span className="text-sm font-semibold text-red-800">PAYMENT REQUIRED</span>
          </div>
        )}

        <div className="space-y-2 text-sm">
          <div className="flex items-baseline justify-between">
            <span className="text-[var(--ink-500)] text-xs uppercase tracking-wider">Ticket</span>
            <span data-testid="online-order-popup-ticket" className="text-2xl font-bold tabular">
              {active.ticket_number != null ? `#${active.ticket_number}` : "—"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[var(--ink-500)]">Source</span>
            <span data-testid="online-order-popup-source" className="font-medium">{sourceLabel}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[var(--ink-500)]">Type</span>
            <span data-testid="online-order-popup-type" className="font-medium">
              {orderTypeLabel}{isPreorder ? " · Preorder" : ""}
            </span>
          </div>
          {scheduledStr && (
            <div className="flex items-center justify-between">
              <span className="text-[var(--ink-500)]">Scheduled</span>
              <span data-testid="online-order-popup-scheduled" className="font-medium tabular">
                {scheduledStr}
              </span>
            </div>
          )}
          {customerName && (
            <div className="flex items-center justify-between">
              <span className="text-[var(--ink-500)]">Customer</span>
              <span data-testid="online-order-popup-customer" className="font-medium">{customerName}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[var(--ink-500)]">Total</span>
            <span data-testid="online-order-popup-total" className="font-semibold tabular">
              {_fmtTotal(active.total)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[var(--ink-500)]">Payment</span>
            <span
              data-testid="online-order-popup-payment"
              data-paid={isUnpaid ? "0" : "1"}
              className={`font-medium ${isUnpaid ? "text-red-700" : "text-emerald-700"}`}
            >
              {paymentLabel}
            </span>
          </div>
        </div>

        {queue.length > 1 && (
          <div
            data-testid="online-order-popup-queue-count"
            className="text-[11px] uppercase tracking-wider text-[var(--ink-500)] text-center"
          >
            {queue.length - 1} more {queue.length - 1 === 1 ? "order" : "orders"} queued
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 pt-1">
          <button
            type="button"
            onClick={onViewOrder}
            data-testid="online-order-popup-view"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-[var(--ink-900)] text-[var(--sand-50)] text-sm font-medium hover:opacity-90"
          >
            <ShoppingCart size={14} /> View Order
          </button>
          <button
            type="button"
            onClick={onOpenKDS}
            data-testid="online-order-popup-kds"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-[rgba(28,25,23,0.16)] text-sm font-medium hover:bg-[var(--sand-100)]"
          >
            <MonitorPlay size={14} /> Open KDS
          </button>
          <button
            type="button"
            onClick={onDismiss}
            data-testid="online-order-popup-dismiss"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-[rgba(28,25,23,0.16)] text-sm font-medium hover:bg-[var(--sand-100)]"
          >
            <X size={14} /> Dismiss
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default OnlineOrderPopupWatcher;
